import { CrateCategoryDto } from '@cratehackers/api-client';
import {
  IonAccordion,
  IonAccordionGroup,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonText,
} from '@ionic/react';
import {
  barChartOutline,
  cogOutline,
  constructOutline,
  cubeOutline,
  gitNetworkOutline,
  helpCircleOutline,
  homeOutline,
  libraryOutline,
  lockOpenOutline,
  peopleOutline,
  radioButtonOn,
} from 'ionicons/icons';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { config } from '../../config';
import { useImportProgress } from '../../context/ImportProgressContext';
import { on, removeListener, run } from '../../helper/electron';
import { useIcons } from '../../hooks/useIcons';
import apiService from '../../services/Api.service';
import djSoftwareService from '../../services/DJSoftware.service';
import { useDispatch } from '../../store/hooks';
import {
  setPreferencesActiveSection,
  setShowPreferences,
} from '../../store/slices/preferences.slice';
import { AppIdentifiers } from '../../store/slices/token-state.slice';
import { RootState } from '../../store/store';
import isElectron from '../../utils/isElectron';
import { openExternalLink } from '../../utils/openExternalLink';
import { ScrollContext } from '../scrollContext/ScrollContext';
import './Menu.scss';

type SeratoCrate = {
  path: string[];
  file: string;
  last_modified_at: string;
};

const Menu: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState<CrateCategoryDto[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryLoadError, setCategoryLoadError] = useState(false);
  const [isSeratoConnected, setIsSeratoConnected] = useState(false);
  const [seratoCrates, setSeratoCrates] = useState<SeratoCrate[]>([]);
  const [loadingSeratoCrates, setLoadingSeratoCrates] = useState(false);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const previousSeratoConnected = useRef(false);
  const previousSeratoAccordionOpen = useRef(false);
  const location = useLocation();
  const { icons } = useIcons();
  const { getActiveJobs } = useImportProgress();

  const activeImportJobs = getActiveJobs();
  const hasActiveImports = activeImportJobs.length > 0;
  const context = useContext(ScrollContext);

  if (!context) return <></>;

  const { scrollToTutorialSection } = context;

  const [isHelpSectionGivenTopPriority, setIsHelpSectionGivenTopPriority] =
    useState(true);

  useEffect(() => {
    const helpButtonLastClickedDate = localStorage.getItem(
      'helpButtonLastClickedDate',
    );
    if (helpButtonLastClickedDate) {
      const stored = new Date(helpButtonLastClickedDate);
      const now = new Date();
      const noOfDaysToCheck = 10;

      const diffTime = now.getTime() - stored.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays > noOfDaysToCheck) setIsHelpSectionGivenTopPriority(false);
      else setIsHelpSectionGivenTopPriority(true);
    }
  }, []);

  const handleHelpButtonClick = () => {
    if (location.pathname !== '/') {
      history.push('/');
      setTimeout(() => scrollToTutorialSection(), 1000);
    } else scrollToTutorialSection();
    const today = new Date().toISOString();
    localStorage.setItem('helpButtonLastClickedDate', today);
    setIsHelpSectionGivenTopPriority(true);
  };

  const overallProgress = useMemo(() => {
    if (activeImportJobs.length === 0) return 0;
    const totalProgress = activeImportJobs.reduce((sum, job) => {
      const jobProgress =
        job.progress.totalTracks > 0
          ? (job.progress.processedTracks / job.progress.totalTracks) * 100
          : 0;
      return sum + jobProgress;
    }, 0);
    return Math.min(100, totalProgress / activeImportJobs.length);
  }, [activeImportJobs]);

  const normalizePath = (path: string) => {
    return path.startsWith('/') ? path.substring(1) : path;
  };

  const isNavActive = (url: string) => {
    const locationPath = normalizePath(location.pathname);
    const normalizedUrl = normalizePath(url);

    // Home page - exact match only
    if (normalizedUrl.length === 0) {
      return locationPath.length === 0;
    }

    // Exact match only for menu items
    return locationPath === normalizedUrl;
  };

  const [version, setVersion] = useState('');
  const [silentUpdateReady, setSilentUpdateReady] = useState<string | null>(
    null,
  );

  const token = useSelector(
    (state: RootState) => state.tokenState[AppIdentifiers.App],
  );

  const hasBusinessPlan = useMemo(() => {
    try {
      if (!token?.accessToken || typeof token.accessToken !== 'string') {
        return false;
      }
      const accessToken = token.accessToken as string;
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const planLevel = payload?.planLevel;
      return planLevel === 'bus_std' || planLevel === 'bus_ann';
    } catch {
      return false;
    }
  }, [token]);

  const hasGroupManagementAccess = useMemo(() => {
    try {
      if (!token?.accessToken || typeof token.accessToken !== 'string') {
        return false;
      }
      const accessToken = token.accessToken as string;
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      return (
        payload?.accessLevel === 'admin' || payload?.accessLevel === 'superuser'
      );
    } catch {
      return false;
    }
  }, [token]);

  useEffect(() => {
    setLoadingCategories(true);
    const chApi = apiService.getApiClient();
    chApi
      .crateControllerGetCrateCategories()
      .then((cats) => {
        setCategories(cats);
        setLoadingCategories(false);
        setCategoryLoadError(false);
      })
      .catch(() => {
        setLoadingCategories(false);
        setCategoryLoadError(true);
      });
  }, []);

  useEffect(() => {
    setVersion(config.VERSION ? config.VERSION : 'unknown');
  }, []);

  useEffect(() => {
    const handleSilentUpdate = (newVersion: string) => {
      setSilentUpdateReady(newVersion);
    };
    on('update-silent-ready', handleSilentUpdate);
    return () => {
      removeListener('update-silent-ready');
    };
  }, []);

  const handleRestartForUpdate = () => {
    run('update-install');
  };

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const chAPI = apiService.getApiClient();
        const groups = await chAPI.usersControllerGetUsersGroups();
        setUserGroups(groups || []);
      } catch {
        setUserGroups([]);
      }
    };
    fetchGroups();
  }, []);

  const crateHackersGroup = useMemo(() => {
    return userGroups.find((g) => g.id === 1 || g.name === 'Crate Hackers');
  }, [userGroups]);

  useEffect(() => {
    const checkAndLoadSeratoCrates = async () => {
      try {
        const selectedSoftware = djSoftwareService.getSelectedSoftware();
        const seratoSelected = selectedSoftware.some((software) =>
          software.toString().toLowerCase().includes('serato'),
        );
        if (seratoSelected) {
          setIsSeratoConnected(true);
          previousSeratoConnected.current = true;
          if (isElectron()) {
            try {
              setLoadingSeratoCrates(true);
              const cratesData = await new Promise<SeratoCrate[]>(
                (resolve, reject) => {
                  const timeout = setTimeout(() => {
                    removeListener('serato/get-crates');
                    reject(new Error('Timeout loading Serato crates'));
                  }, 10000);
                  on('serato/get-crates', (data: SeratoCrate[]) => {
                    clearTimeout(timeout);
                    removeListener('serato/get-crates');
                    resolve(data || []);
                  });
                  run('serato/get-crates');
                },
              );
              setSeratoCrates(cratesData);
            } catch {
              setSeratoCrates([]);
            } finally {
              setLoadingSeratoCrates(false);
            }
          }
        }
      } catch {
        // Ignore errors
      }
    };
    checkAndLoadSeratoCrates();
  }, []);

  const loadSeratoCrates = useCallback(async () => {
    if (!isElectron() || !isSeratoConnected) {
      setSeratoCrates([]);
      return;
    }
    try {
      setLoadingSeratoCrates(true);
      const cratesData = await new Promise<SeratoCrate[]>((resolve, reject) => {
        const timeout = setTimeout(() => {
          removeListener('serato/get-crates');
          reject(new Error('Timeout loading Serato crates'));
        }, 10000);
        on('serato/get-crates', (data: SeratoCrate[]) => {
          clearTimeout(timeout);
          removeListener('serato/get-crates');
          resolve(data || []);
        });
        run('serato/get-crates');
      });
      setSeratoCrates(cratesData);
    } catch {
      setSeratoCrates([]);
    } finally {
      setLoadingSeratoCrates(false);
    }
  }, [isSeratoConnected]);

  const handleSeratoCratesAccordionChange = useCallback(
    (ev: CustomEvent) => {
      const openAccordions = ev.detail.value;
      const isSeratoOpen =
        Array.isArray(openAccordions) &&
        openAccordions.includes('serato-crates');
      if (isSeratoOpen && !previousSeratoAccordionOpen.current) {
        loadSeratoCrates();
      }
      previousSeratoAccordionOpen.current = isSeratoOpen;
    },
    [loadSeratoCrates],
  );

  useEffect(() => {
    const checkSeratoConnection = () => {
      try {
        const selectedSoftware = djSoftwareService.getSelectedSoftware();
        const seratoSelected = selectedSoftware.some((software) =>
          software.toString().toLowerCase().includes('serato'),
        );
        if (seratoSelected !== previousSeratoConnected.current) {
          previousSeratoConnected.current = seratoSelected;
          setIsSeratoConnected(seratoSelected);
          if (seratoSelected) {
            loadSeratoCrates();
          } else {
            setSeratoCrates([]);
            previousSeratoAccordionOpen.current = false;
          }
        }
      } catch {
        if (previousSeratoConnected.current !== false) {
          previousSeratoConnected.current = false;
          setIsSeratoConnected(false);
          setSeratoCrates([]);
          previousSeratoAccordionOpen.current = false;
        }
      }
    };
    checkSeratoConnection();
    const interval = setInterval(checkSeratoConnection, 10000);
    return () => {
      clearInterval(interval);
      removeListener('serato/get-crates');
    };
  }, [loadSeratoCrates]);

  // Open preferences modal to a specific section
  const openPreferencesSection = useCallback(
    (section: string) => {
      dispatch(setPreferencesActiveSection(section));
      dispatch(setShowPreferences(true));
    },
    [dispatch],
  );

  // Check for updates (electron only)
  const checkForUpdates = useCallback(() => {
    if (isElectron()) {
      run('update-check');
    }
  }, []);

  // Navigation structure based on new UX requirements
  const navItems = useMemo(
    () => ({
      // HOME - top-level, links to /
      home: {
        id: 'home',
        title: 'Home',
        url: '/',
        icon: homeOutline,
      },

      // CRATES section
      crates: {
        id: 'crates',
        title: 'Crates',
        icon: cubeOutline,
        items: [
          { id: 'mixable-crates', title: 'Mixable crates', url: '/crates' },
          {
            id: 'crate-builder',
            title: 'Crate builder',
            url: '/my-crates/spotify',
          },
          { id: 'your-crates', title: 'Your crates', url: '/my-crates' },
        ],
      },

      // CHARTS section (trending /charts)
      charts: {
        id: 'charts',
        title: 'Charts',
        icon: barChartOutline,
        items: [
          {
            id: 'trending-charts',
            title: 'Trending charts',
            url: '/charts',
          },
          {
            id: 'favorite-charts',
            title: 'Favorite charts',
            url: '/charts/favorites',
          },
        ],
      },

      // LIBRARY section (desktop only)
      music: {
        id: 'music',
        title: 'Library',
        icon: libraryOutline,
        desktopOnly: true,
        items: [
          { id: 'library', title: 'Files', url: '/my-library' },
          { id: 'your-crates-music', title: 'Your crates', url: '/my-crates' },
          {
            id: 'music-sources',
            title: 'Music sources',
            url: '/my-library/sources',
          },
        ],
      },

      // AUTOMATIONS — root section above Tools (desktop only)
      automations: {
        id: 'automations',
        title: 'Automations',
        url: '/automations',
        icon: gitNetworkOutline,
        desktopOnly: true,
      },

      // TOOLS section
      tools: {
        id: 'tools',
        title: 'Tools',
        icon: constructOutline,
        items: [
          {
            id: 'crate-builder-tool',
            title: 'Crate builder',
            url: '/my-crates/spotify',
          },
          {
            id: 'smpl-import',
            title: 'SMPL import',
            url: '/my-crates/smpl',
          },
          {
            id: 'djep-import',
            title: 'DJEP import',
            url: '/my-crates/djep',
          },
          {
            id: 'ai-duplicate-cleaner',
            title: 'AI duplicate cleaner',
            url: '/resolve-duplicates',
            desktopOnly: true,
          },
          {
            id: 'folder-flattener',
            title: 'Folder flattener',
            url: '/tools/folder-flattener',
            desktopOnly: true,
          },
          {
            id: 'recycle-bin',
            title: 'Recycle bin',
            url: '/recycle-bin',
            desktopOnly: true,
          },
          {
            id: 'website',
            title: 'Website',
            url: 'https://x.cratehackers.com/#/',
            external: true,
            desktopOnly: true,
          },
          {
            id: 'magic-playlist',
            title: 'Magic playlist',
            url: 'https://magicplaylist.co',
            external: true,
          },
          {
            id: 'business-plan',
            title: hasBusinessPlan
              ? 'Business plan dashboard'
              : 'Business plan',
            url: hasBusinessPlan
              ? '/business-plan'
              : 'https://www.cratehackers.com/businessplanupgrade',
            external: !hasBusinessPlan,
          },
          ...(hasBusinessPlan
            ? [
                {
                  id: 'hackers-labs',
                  title: 'Labs',
                  url: '/labs',
                },
              ]
            : []),
          {
            id: 'algRTHM',
            title: 'algRTHM',
            url: 'https://alg.rthm.studio/algorithms',
            external: true,
          },
          {
            id: 'banger-button',
            title: 'Banger Button',
            url: 'https://bangerbutton.com/',
            external: true,
          },
        ],
      },

      // SETTINGS section - opens preferences modal
      settings: {
        id: 'settings',
        title: 'Settings',
        icon: cogOutline,
        items: [
          {
            id: 'music-sources-settings',
            title: 'Music sources',
            url: '/my-library/sources',
            desktopOnly: true,
          },
          {
            id: 'track-matcher',
            title: 'Track matcher',
            preferencesSection: 'track-matcher',
            desktopOnly: true,
          },
          {
            id: 'dj-software',
            title: 'DJ software',
            preferencesSection: 'dj-software',
            desktopOnly: true,
          },
          {
            id: 'record-pool-integrations',
            title: 'Record pool integrations',
            preferencesSection: 'service-integrations',
            desktopOnly: true,
          },
          {
            id: 'record-pools',
            title: 'Record pools',
            preferencesSection: 'pool-selection',
          },
          {
            id: 'account-help',
            title: 'Account',
            preferencesSection: 'account-help',
          },
          {
            id: 'check-updates',
            title: 'Check for updates',
            preferencesSection: 'app-update',
            desktopOnly: true,
          },
          { id: 'reset', title: 'Reset', preferencesSection: 'danger-zone' },
          { id: 'logout', title: 'Sign out', url: '/logout' },
        ],
      },

      // HELP section
      help: {
        id: 'help',
        title: 'Help',
        icon: helpCircleOutline,
        items: [
          {
            id: 'help-articles',
            title: 'Help articles',
            url: 'https://help.cratehackers.com',
            external: true,
          },
          {
            id: 'videos',
            title: 'Videos',
            url: 'https://www.youtube.com/@cratehackers?sub_confirmation=1',
            external: true,
          },
          {
            id: 'contact-support',
            title: 'Contact support',
            url: 'https://help.cratehackers.com/support/tickets/new',
            external: true,
          },
          {
            id: 'account-help',
            title: 'Account Help',
            url: 'https://cancellation-project.vercel.app/account-help',
            external: true,
          },
          {
            id: 'report-bug',
            title: 'Report bug',
            url: '/feedback/bug',
          },
          {
            id: 'feature-request',
            title: 'Feature request',
            url: '/feedback/feature-request',
          },
        ],
      },

      // COMMUNITY section (order aligned with develop: YT, Twitch, FB, DJ group, Blog)
      community: {
        id: 'community',
        title: 'Community',
        icon: peopleOutline,
        items: [
          {
            id: 'youtube',
            title: 'YouTube',
            url: 'https://www.youtube.com/@cratehackers?sub_confirmation=1',
            external: true,
          },
          {
            id: 'twitch',
            title: 'Twitch',
            url: 'https://www.twitch.tv/thecratehackers',
            external: true,
          },
          {
            id: 'private-facebook',
            title: 'Private Facebook',
            url: 'https://www.facebook.com/groups/cratehackers',
            external: true,
          },
          {
            id: 'dj-playlist-group',
            title: 'DJ Playlist Group',
            url: 'https://www.facebook.com/groups/260532984057599',
            external: true,
          },
          {
            id: 'blog',
            title: 'Blog',
            url: 'https://cratehackers.blog',
            external: true,
          },
        ],
      },

      // Live Hackathons — top-level row below Community (same pattern as Home)
      liveHackathon: {
        id: 'live-hackathon',
        title: 'Live Hackathons',
        url: 'https://www.cratehackathon.com',
      },

      // ADMIN sections (footer, orange)
      curators: crateHackersGroup
        ? {
            id: 'curators',
            title: 'Curators',
            icon: lockOpenOutline,
            isAdmin: true,
            items: [
              {
                id: 'ch-dashboard',
                title: 'Dashboard',
                url: `/crates/team/${crateHackersGroup.id}`,
              },
              {
                id: 'ch-labs',
                title: 'Crate Hackers Labs',
                url: '/crate-hackers-labs',
              },
            ],
          }
        : null,

      admin: hasGroupManagementAccess
        ? {
            id: 'admin',
            title: 'Admin',
            icon: lockOpenOutline,
            isAdmin: true,
            items: [
              {
                id: 'group-management',
                title: 'Group management',
                url: '/admin/group-management',
              },
              {
                id: 'announcements',
                title: 'Announcements',
                url: '/admin/announcements',
              },
              {
                id: 'tutorial-videos',
                title: 'Tutorial videos - BETA',
                url: '/admin/tutorial-videos',
              },
              {
                id: 'known-issues',
                title: 'Known issues',
                url: '/admin/known-issues',
              },
              {
                id: 'app-management',
                title: 'App management',
                url: '/admin/app-management',
              },
              {
                id: 'labs-admin',
                title: 'Labs',
                url: '/labs/admin',
              },
            ],
          }
        : null,
    }),
    [hasBusinessPlan, crateHackersGroup, hasGroupManagementAccess],
  );

  // Render a sub-item (no icon)
  const renderSubItem = (
    item: {
      id: string;
      title: string;
      url?: string;
      external?: boolean;
      preferencesSection?: string;
      action?: string;
      desktopOnly?: boolean;
    },
    index: number,
  ) => {
    // Skip desktop-only items on web
    if (item.desktopOnly && !isElectron()) {
      return null;
    }

    // Handle preferences modal items
    if (item.preferencesSection) {
      return (
        <IonMenuToggle key={index} autoHide={false}>
          <IonItem
            id={item.id}
            className="sub-item"
            lines="none"
            detail={false}
            button
            onClick={() => openPreferencesSection(item.preferencesSection!)}
          >
            <IonLabel>{item.title}</IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    }

    // Handle action items (like check for updates)
    if (item.action === 'checkUpdates') {
      return (
        <IonMenuToggle key={index} autoHide={false}>
          <IonItem
            id={item.id}
            className="sub-item"
            lines="none"
            detail={false}
            button
            onClick={checkForUpdates}
          >
            <IonLabel>{item.title}</IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    }

    // Handle external links
    if (item.external && item.url) {
      return (
        <IonMenuToggle key={index} autoHide={false}>
          <IonItem
            id={item.id}
            className="sub-item external-link"
            lines="none"
            detail={false}
            button
            onClick={() => openExternalLink(item.url!)}
          >
            <IonLabel>
              {item.title}
              <span className="external-indicator"> ↗</span>
            </IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    }

    // Handle internal navigation
    if (item.url) {
      return (
        <IonMenuToggle key={index} autoHide={false}>
          <IonItem
            id={item.id}
            className={`sub-item ${isNavActive(item.url) ? 'active' : ''}`}
            lines="none"
            detail={false}
            routerLink={item.url}
            routerDirection="none"
          >
            <IonLabel>{item.title}</IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    }

    return null;
  };

  // Render an accordion section
  const renderAccordionSection = (
    section: {
      id: string;
      title: string;
      icon: string;
      items: any[];
      isAdmin?: boolean;
      desktopOnly?: boolean;
    } | null,
  ) => {
    if (!section) return null;
    if (section.desktopOnly && !isElectron()) return null;

    return (
      <IonAccordion value={section.id} key={section.id}>
        <IonItem slot="header" className="accordion-header">
          <IonIcon
            icon={section.icon}
            slot="start"
            style={{ fontSize: '18px' }}
          />
          <IonLabel>{section.title}</IonLabel>
        </IonItem>
        <IonList slot="content" className="accordion-content">
          {section.items.map((item, index) => renderSubItem(item, index))}
        </IonList>
      </IonAccordion>
    );
  };

  return (
    <IonMenu
      contentId="main"
      type="overlay"
      onIonWillOpen={() => setMenuOpen(true)}
      onIonDidClose={() => setMenuOpen(false)}
      className={`new-menu${menuOpen ? ' menu-overlay-open' : ''}`}
    >
      <IonContent>
        {/* Main navigation accordions - only one open at a time */}
        <IonAccordionGroup className="main-accordion-group">
          {/* HOME - Wrapped in accordion for consistent styling */}
          <IonAccordion value="home" className="home-accordion">
            <IonMenuToggle slot="header" autoHide={false}>
              <IonItem
                id={navItems.home.id}
                className={`accordion-header ${isNavActive(navItems.home.url) ? 'active' : ''}`}
                lines="none"
                detail={false}
                routerLink={navItems.home.url}
                routerDirection="none"
              >
                <IonIcon
                  icon={navItems.home.icon}
                  slot="start"
                  style={{ fontSize: '18px' }}
                />
                <IonLabel>{navItems.home.title}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <div slot="content"></div>
          </IonAccordion>
          {renderAccordionSection(navItems.crates)}
          {renderAccordionSection(navItems.charts)}
          {renderAccordionSection(navItems.music)}
          {/* Automations menu item temporarily hidden (feature not ready for release) */}
          {false && isElectron() && (
            <IonAccordion value="automations" className="automations-accordion">
              <IonMenuToggle slot="header" autoHide={false}>
                <IonItem
                  id={navItems.automations.id}
                  className={`accordion-header ${isNavActive(navItems.automations.url) ? 'active' : ''}`}
                  lines="none"
                  detail={false}
                  routerLink={navItems.automations.url}
                  routerDirection="none"
                >
                  <IonIcon
                    icon={navItems.automations.icon}
                    slot="start"
                    style={{ fontSize: '18px' }}
                  />
                  <IonLabel>{navItems.automations.title}</IonLabel>
                </IonItem>
              </IonMenuToggle>
              <div slot="content"></div>
            </IonAccordion>
          )}
          {renderAccordionSection(navItems.tools)}
          {renderAccordionSection(navItems.settings)}
          {renderAccordionSection(navItems.help)}
          {renderAccordionSection(navItems.community)}
          <IonAccordion
            value="live-hackathon"
            className="live-hackathon-accordion"
          >
            <IonMenuToggle slot="header" autoHide={false}>
              <IonItem
                id={navItems.liveHackathon.id}
                className={`accordion-header live-hackathon external-link${isNavActive(navItems.liveHackathon.url) ? ' active' : ''}`}
                lines="none"
                detail={false}
                button
                onClick={() => openExternalLink(navItems.liveHackathon.url)}
              >
                {[
                  <IonIcon
                    key="live-hackathon-icon"
                    icon={radioButtonOn}
                    slot="start"
                    style={{ fontSize: '18px' }}
                    className="live-hackathon-ion-icon"
                    aria-hidden="true"
                  />,
                  <IonLabel
                    key="live-hackathon-label"
                    className="live-hackathon-menu-label"
                  >
                    {navItems.liveHackathon.title}
                  </IonLabel>,
                ]}
              </IonItem>
            </IonMenuToggle>
            <div slot="content"></div>
          </IonAccordion>
        </IonAccordionGroup>

        {/* Admin sections at the bottom */}
        {(navItems.curators || navItems.admin) && (
          <IonAccordionGroup className="admin-accordion-group">
            {renderAccordionSection(navItems.curators)}
            {renderAccordionSection(navItems.admin)}
          </IonAccordionGroup>
        )}
      </IonContent>

      <IonFooter>
        <div className="footer-items">
          <div className="version-wrapper">
            <span className="version-label">Version</span>
            <span className="version-number">{version}</span>
            {silentUpdateReady && (
              <span
                className="silent-update-indicator"
                onClick={handleRestartForUpdate}
                title={`Update to ${silentUpdateReady} ready - click to restart`}
              >
                ↑ {silentUpdateReady}
              </span>
            )}
          </div>
          <div className="copyright-wrapper">
            <span>
              Copyright Crate Hackers &copy; {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </IonFooter>
    </IonMenu>
  );
};

export default Menu;
